import type { ReactNode } from 'react';
import { StatusLamp } from './StatusLamp.tsx';
import type { LampTone } from './StatusLamp.tsx';
import { cx } from './cx.ts';

/**
 * Status chip: a bordered pill with an optional board lamp. Text stays
 * lowercase/mixed-case — uppercase+tracking is banned for CJK labels
 * (DESIGN.md §3).
 */
export type ChipTone = 'ok' | 'warn' | 'bad' | 'signal' | 'neutral';

const TONE_CLASSES: Record<ChipTone, string> = {
  ok: 'border-ok-line bg-ok-dim/50 text-ok',
  warn: 'border-warn-line bg-warn-dim/50 text-warn',
  bad: 'border-bad-line bg-bad-dim/50 text-bad',
  signal: 'border-signal-deep/50 bg-signal-dim text-signal',
  neutral: 'border-line bg-transparent text-paper-low',
};

const LAMP_TONE: Record<ChipTone, LampTone> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
  signal: 'signal',
  neutral: 'off',
};

interface Props {
  readonly tone: ChipTone;
  readonly children: ReactNode;
  readonly lamp?: boolean;
  readonly pulse?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

export function Chip({ tone, children, lamp = true, pulse = false, className, testId }: Props): JSX.Element {
  return (
    <span
      data-testid={testId}
      className={cx(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-micro font-medium',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {lamp ? <StatusLamp tone={LAMP_TONE[tone]} size="sm" pulse={pulse} /> : null}
      {children}
    </span>
  );
}
